import React, { useState, useEffect, Suspense } from 'react';
import {
    Routes, Route, Navigate, useNavigate, useLocation,
} from 'react-router-dom';
import { track, identify } from '@lf/analytics-events';
import { setUserId, usePageTracking } from './analytics';
import './App.css';
import ParentPage from './pages/ParentPage';
import LoginPage from './pages/LoginPage';
import TrialPage from './pages/TrialPage';
import IndexPageV1 from './pages/IndexPageV1';
import ChildSignupPage from './pages/ChildSignupPage';
import ChildAgeCheckPage from './pages/ChildAgeCheckPage';
import ChildLinkPage from './pages/ChildLinkPage';
import ParentConsentPage from './pages/ParentConsentPage';
import ShareLandingPage from './pages/ShareLandingPage';
import Header from './components/Header';
import DevelopedWithYouTube from './components/DevelopedWithYouTube';
import { HeaderProvider } from './contexts/HeaderContext';
import { UserProvider } from './contexts/UserContext';
import lfApi from '@lf/api-client/lfApi';
import { getUserProfile } from './api/lfApiUser';
// Which queue labels which suite, and where each used to live. Imported
// statically rather than behind the lazy gate below because it is a table of
// constants with no React in it — what that gate protects is the eval CHUNK,
// and five strings are not it.
import { evalPath } from './lib/evalReview/routes';
import {
    QUEUES, REC_QUALITY, REC_VIDEO_GRADES, CONTENT_QUALITY, THUMBNAIL_APPEAL,
    WITHIN_TOPIC, legacyLabelPath,
} from './lib/evalReview/labelRoutes';

// Eval grading UIs — available in dev and staging, never in prod. The lazy
// import + the matching <Route> live inside this branch so CRA's build-time
// dead-code elimination drops the chunk from prod bundles. `NODE_ENV` covers
// local dev; staging is itself a production build, so it's additionally gated
// on the runtime env flag `REACT_APP_LF_ENV === 'staging'`. Both operands are
// inlined by CRA at build time, so the branch still constant-folds away for
// prod. The gate FAILS CLOSED: a missing/unknown flag → hidden. Backed by the
// Flask `/_eval/*` blueprints, which are registered only in dev/staging and
// 404 in prod regardless of what the FE attempts to call.
const EVAL_UI_ENABLED = process.env.NODE_ENV !== 'production'
    || process.env.REACT_APP_LF_ENV === 'staging';

const RecEvalPage = EVAL_UI_ENABLED
    ? React.lazy(() => import('./pages/RecEvalPage'))
    : null;

// Manual thumbnail-appeal grading UI. Same gate/DCE as RecEvalPage; backed by
// the /_eval/thumbnail blueprint.
const ThumbnailEvalPage = EVAL_UI_ENABLED
    ? React.lazy(() => import('./pages/ThumbnailEvalPage'))
    : null;

// Manual per-video content grading UI (quality + grade_fit). Same gate/DCE as
// RecEvalPage; backed by the /_eval/content blueprint.
const ContentEvalPage = EVAL_UI_ENABLED
    ? React.lazy(() => import('./pages/ContentEvalPage'))
    : null;

// Within-topic per-video grading queue (quality + grade fit, absolute). Same
// gate/DCE as RecEvalPage; backed by the /_eval/within_topic blueprint.
const WithinTopicEvalPage = EVAL_UI_ENABLED
    ? React.lazy(() => import('./pages/WithinTopicEvalPage'))
    : null;

// Human label review — the grading surface every conforming eval suite needs
// (§4.13 of the eval standard). Same gate/DCE as RecEvalPage; backed by the
// /_eval/review blueprint. Its siblings each grade one bespoke thing; this one
// is generic over every suite, because it reads the exported case records —
// which is why it is mounted at /_eval/all rather than under a suite's name.
const EvalReviewPage = EVAL_UI_ENABLED
    ? React.lazy(() => import('./pages/EvalReviewPage'))
    : null;

// The shell every eval route renders in.
const EvalShell = EVAL_UI_ENABLED
    ? React.lazy(() => import('./cards/evalReview/EvalShell'))
    : null;
const QueueLabelRoute = EVAL_UI_ENABLED
    ? React.lazy(() => import('./cards/evalReview/QueueLabelRoute'))
    : null;

// Each queue, as the thing its suite's label route renders. The item id is
// resolved from the address by `QueueLabelRoute` and handed down as a prop:
// the queue reads what it is showing from ONE place, and that place is the
// address, which is what §4.13.7 is for.
const QUEUE_PAGES = EVAL_UI_ENABLED ? [
    {
        suite: REC_VIDEO_GRADES,
        render: (itemId, mode) => <RecEvalPage itemId={itemId} mode={mode} />,
    },
    {
        suite: REC_QUALITY,
        render: (itemId, mode) => (
            <RecEvalPage queue="lessons" itemId={itemId} mode={mode} />
        ),
    },
    {
        suite: CONTENT_QUALITY,
        render: (itemId, mode) => <ContentEvalPage itemId={itemId} mode={mode} />,
    },
    {
        suite: THUMBNAIL_APPEAL,
        render: (itemId, mode) => <ThumbnailEvalPage itemId={itemId} mode={mode} />,
    },
    {
        suite: WITHIN_TOPIC,
        render: (itemId, mode) => <WithinTopicEvalPage itemId={itemId} mode={mode} />,
    },
] : [];

// Forwards the surface's previous home. Kept as a redirect rather than a second
// mount so there is one canonical URL: two live paths for one page is how a
// reviewer ends up with two tabs of the same sitting and grades it twice.
function EvalAllRedirect() {
    const { pathname } = useLocation();
    // The path is carried across; the query string is not. Nothing on these
    // surfaces reads one any more — `?case=` was the last, and a case is a path
    // segment (§4.13.7) — so forwarding it would preserve state the grammar
    // says is not an address.
    return (
        <Navigate replace to={pathname.replace('/_eval/review', '/_eval/all')} />
    );
}

// The four bespoke queues moved under the suites they write.
//
// A queue is not a thing; it is a suite being labelled, so its address is
// `/suite/<suite>/label` and there is no second grammar for it. These forward
// the addresses those queues used to answer to — pasted in tickets, printed by
// the CLI and bookmarked — including the item, whose id is rebuilt as the case
// id the grammar addresses: `<suite>/<item>`.
function QueueRedirect() {
    const { pathname } = useLocation();
    return <Navigate replace to={legacyLabelPath(pathname)} />;
}

// Every eval route, in the internal tool's own shell.
//
// Outside the consumer app entirely (L10 → §4.13.6a). Two of the three things
// that buys are normative: no product analytics on a labelling session, and no
// product access control — region, entitlement, subscription — between a
// labeler and a draw. Both used to apply here because these routes rendered
// inside the branch that enforces them, and a reviewer outside the US got
// "Sorry, Learnforest is not yet available in your region" instead of the
// grading queue.
//
// The non-production gate stays per route and is tested per route: a gate that
// is a property of a SURFACE gets forgotten the moment the surface grows a
// page, and what leaks is a bench — canary keys, unlabelled cases and this
// domain's own case payloads.
function EvalRoutes() {
    if (!EVAL_UI_ENABLED) return null;
    const page = (Page, props = {}) => (
        <Suspense fallback={<div className="p-4 text-gray-500">Loading eval…</div>}>
            <Page {...props} />
        </Suspense>
    );
    return (
        <Suspense fallback={<div className="p-4 text-gray-500">Loading eval…</div>}>
            <EvalShell>
                <Routes>
                    {/* One index, not two. `/_eval` listed the five SURFACES
                        and what each writes; `/_eval/all` lists the suites and
                        the datasets — the grammar's two families — and now
                        carries the writes/read-by facts on the suite rows,
                        where they are true by construction rather than true of
                        whichever surfaces that page happened to know about. */}
                    <Route path="/_eval" element={<Navigate replace to="/_eval/all" />} />
                    {/* Labelling hangs off the suite whose store it writes.
                        Five queues over five suites, each mounted at
                        `/suite/<suite>/label` — the address §4.13.7 already
                        publishes — rather than at four top-level applications
                        with sub-grammars of their own. These come BEFORE
                        `/_eval/all/*` so the generic surface keeps every suite
                        that has no bespoke queue. */}
                    {QUEUE_PAGES.map(({ suite, render }) => (
                        <Route
                            key={suite}
                            path={`${evalPath.label(suite)}/*`}
                            element={page(QueueLabelRoute, { suite, render })}
                        />
                    ))}
                    {/* The other labelling address: the cases this suite has
                        ALREADY decided, for re-checking. A second route rather
                        than a mode on the first, because it is a different set
                        of cases and a labeler should be able to link to it —
                        and because a queue whose corpus is fully decided draws
                        nothing, which is how "re-check labeled cases" used to
                        land on "there is nothing left to draw". */}
                    {QUEUE_PAGES.map(({ suite, render }) => (
                        <Route
                            key={`${suite}-labeled`}
                            path={`${evalPath.labeled(suite)}/*`}
                            element={page(QueueLabelRoute, {
                                suite, render, mode: 'labeled',
                            })}
                        />
                    ))}
                    {QUEUES.map(({ legacy }) => (
                        <Route key={legacy} path={`${legacy}/*`} element={<QueueRedirect />} />
                    ))}
                    <Route path="/_eval/all/*" element={page(EvalReviewPage)} />
                    {/* The surface used to live at /_eval/review. Links to it
                        are pasted in tickets, printed by the review CLI and
                        bookmarked, so the old path keeps working and forwards —
                        including deep links, whose tail is carried across. */}
                    <Route path="/_eval/review/*" element={<EvalAllRedirect />} />
                    {/* An address that does not resolve says so, and is never
                        answered with a neighbouring entity (§4.13.7). */}
                    <Route path="/_eval/*" element={
                        <div className="p-4 text-sm text-gray-700">
                            <p className="font-semibold">No eval surface at this address.</p>
                            <p className="mt-1">
                                The five that exist are in the bar above.
                            </p>
                        </div>
                    } />
                </Routes>
            </EvalShell>
        </Suspense>
    );
}

async function getUser() {
    try {
        const response = await lfApi.get('/auth/user');
        if (response.status === 200) {
            return response.data;
        }
        else {
            console.error('Failed to fetch user profile');
        }
    }
    catch (error) {
        console.error('Error fetching user profile:', error);
    }

    return null;
}

function App() {
    const [isUS, setIsUS] = useState(null);
    const [isUserLoading, setIsUserLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [subscriptionAction, setSubscriptionAction] = useState({action: 'none'});

    const navigate = useNavigate();
    const location = useLocation();
    const isIndexPage = location.pathname === '/www_v1';
    // Share pages render outside the auth guard. fetchUser still runs so
    // the landing knows whether a parent is signed in, but the render
    // does not gate on isUserLoading.
    const isSharePage = location.pathname.startsWith('/share/');
    // The internal tool. Rendered in its own shell, outside the consumer
    // header, the region gate and the product analytics.
    const isEvalPage = location.pathname === '/_eval'
        || location.pathname.startsWith('/_eval/');

    const fetchUser = async () => {
        const user = await getUser();
        if (user) {
            if (user.geo_data) {
                setIsUS(user.geo_data.country === 'US');
            }
            if (user.subscription_action) {
                setSubscriptionAction(user.subscription_action);
            }
            if (user.user_profile_id) {
                await setUserId(user.user_profile_id);
                track('login', { method: 'email_token' });

                const profile = await getUserProfile();
                setUserProfile(profile);
            }
        }

        setIsUserLoading(false);
    }

    const logout = () => {
        track('logout', {});
        identify(null, {});
        setUserProfile(null);
        setSubscriptionAction({action: 'none'});
    };

    const onUserProfile = () => {
        navigate('/setting');
    };

    const onChildProfile = (username) => {
        navigate(`/profile/${username}`);
    };

    const onAddChildProfile = () => {
        navigate('/add_profile');
    };

    useEffect(() => {
        if (isIndexPage) {
            setIsUserLoading(false);
        } else {
            // Share landing still needs auth probing so signed-in parents
            // see the multi-target picker, but its rendering does not
            // depend on isUserLoading.
            fetchUser();
        }

        // Per design §C: clear identity on tab close so a second user on
        // the same browser doesn't inherit the prior user_id.
        const onPageHide = () => identify(null, {});
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, [isIndexPage]); // Skip auth bootstrapping for static homepage routes.

    // Not on an eval route. Internal labelling is not product usage, and a
    // funnel that counts a grading session is measuring its own operator
    // (L10 → §4.13.6a, and §4.13.3's rule running the other way).
    usePageTracking(!isEvalPage);

    const userContextValue = {
        userProfile,
        setUserProfile,
        subscriptionAction,
        setSubscriptionAction,
        fetchUser,
        logout,
        isUserLoading,
        isUS,
    };

    // Before the consumer shell, and instead of it. Everything below this line
    // — the header, the 1024px cap, the marketing footer, the region gate and
    // the product analytics — is the consumer app, and none of it belongs
    // around a grading queue or a statistical report.
    if (isEvalPage) {
        return <EvalRoutes />;
    }

    return (
        <div>
            <div className="App flex flex-col h-screen">
              <UserProvider value={userContextValue}>
                <HeaderProvider>
                    <Header
                        userProfile={userProfile}
                        subscriptionAction={subscriptionAction}
                        onUserProfile={onUserProfile}
                        onChildProfile={onChildProfile}
                        onAddChildProfile={onAddChildProfile} />

                    <main className={`flex-1 w-full ${isIndexPage ? 'pt-0' : 'p-4 pt-8 mx-auto max-w-screen-lg'}`}>
                        {
                            isIndexPage ? (
                                <Routes>
                                    <Route path="/www_v1" element={
                                        <IndexPageV1 />
                                    } />
                                </Routes>
                            ) : isSharePage ? (
                                <Routes>
                                    <Route path="/share/:token" element={
                                        <ShareLandingPage />
                                    } />
                                </Routes>
                            ) : isUS !== false ? (
                                isUserLoading
                                    ? <div>Loading...</div>
                                    : <Routes>
                                        <Route path="/login" element={
                                            <LoginPage />
                                        } />
                                        <Route path="/trial" element={
                                            <TrialPage />
                                        } />
                                        <Route path="/student/signup" element={
                                            <ChildSignupPage />
                                        } />
                                        <Route path="/student/age-check" element={
                                            <ChildAgeCheckPage />
                                        } />
                                        <Route path="/parent-consent/:token" element={
                                            <ParentConsentPage />
                                        } />
                                        <Route path="/child-link/:token" element={
                                            userProfile
                                                ? <ChildLinkPage />
                                                : <LoginPage />
                                        } />
                                        <Route path="/*" element={
                                            userProfile
                                                ? <ParentPage />
                                                : <LoginPage />
                                        } />
                                    </Routes>
                            ) : (
                                <div className="bg-red-500 text-white p-4">
                                    Sorry, Learnforest is not yet available in your region.
                                </div>
                            )
                        }
                    </main>

                    <footer className="bg-branch text-white text-center p-4">
                        <div className="mx-auto flex max-w-screen-lg flex-col items-center justify-center gap-3 sm:flex-row-reverse sm:justify-between">
                            <div className="sm:text-right">
                                <p>
                                    &copy; 2026 Learnforest
                                </p>
                                <p className="text-xs mt-1">
                                    <a href="https://learnforest.app/tos.html" target="_blank" rel="noreferrer">Terms of Service</a>
                                    <span className="mx-2">|</span>
                                    <a href="https://learnforest.app/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
                                    <span className="mx-2">|</span>
                                    <a href="mailto:support@learnforest.app">Support</a>
                                </p>
                            </div>
                            <DevelopedWithYouTube variant="light" imageClassName="h-auto w-[207px]" />
                        </div>
                    </footer>
                </HeaderProvider>
              </UserProvider>
            </div>
        </div>
    );
}

export default App;
